export const fetchChannels = (pubnub, userId) => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async resolve => {
    const channels = {}
    const metadata = await pubnub.objects.getAllChannelMetadata({
      filter: `id LIKE "*${userId}*"`,
      include: { customFields: true }
    })
    metadata.data.forEach(({ id, name, updated, custom }) => {
      channels[id] = {
        id,
        name,
        updated,
        custom: { ...channels[id]?.custom, ...custom }
      }
    })
    resolve(channels)
  })
}

export const createDirectChannel = (pubnub, userId, chatWithId, customData) => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async resolve => {
    const channel = createChannelId(userId, chatWithId)
    await pubnub.objects.setChannelMetadata({
      channel,
      data: customData
    })
    await pubnub.objects.setChannelMembers({
      channel,
      uuids: [{ id: userId }, { id: `${chatWithId}` }]
    })
    await pubnub.channelGroups.addChannels({
      channels: [channel],
      channelGroup: userId
    })
    resolve({ channel: channel })
  })
}

export const createChannelId = (userId, chatWithId) => {
  return [userId, chatWithId].sort().join("-")
}

export const publishMessage = (pubnub, message, channel, metaInfo, uuid) => {
  return new Promise(resolve => {
    pubnub.publish(
      {
        message: message,
        channel: channel,
        meta: metaInfo
      },
      (status, response) => {
        // Update lastReadTimetoken so your own message doesn't count as unread
        if (uuid) {
          updateChannelLastReadTimetoken(pubnub, channel, uuid).catch(err => {
            // eslint-disable-next-line no-console
            console.error("Failed to update lastReadTimetoken:", err)
          })
        }
        return resolve(response)
      }
    )
  })
}

export const publishFile = (pubnub, file, channel, message, metaInfo) => {
  return new Promise(() => {
    pubnub.sendFile({
      file: file,
      channel: channel,
      message: message,
      meta: metaInfo
    })
  })
}

export const downloadUploadedFile = (pubnub, fileParams) => {
  return pubnub.downloadFile(fileParams)
}

export const timeSince = date => {
  if (!date) {
    return null
  }
  const seconds = Math.floor((new Date() - date) / 1000)
  let interval = seconds / 31536000
  if (interval > 1) {
    return Math.floor(interval) + "y"
  }
  interval = seconds / 2592000
  if (interval > 1) {
    return Math.floor(interval) + "m"
  }
  interval = seconds / 86400
  if (interval > 1) {
    return Math.floor(interval) + "d"
  }
  interval = seconds / 3600
  if (interval > 1) {
    return Math.floor(interval) + "h"
  }
  interval = seconds / 60
  if (interval > 1) {
    return Math.floor(interval) + "m"
  }
  return Math.floor(seconds) + "s"
}

export const showTime = date => {
  if (!date) {
    return null
  }
  const d = new Date(date)
  const currentDate = new Date().setHours(0, 0, 0, 0)
  const providedDate = new Date(d).setHours(0, 0, 0, 0)
  const seconds = Math.floor((currentDate - providedDate) / 1000)
  const hours = d.getHours()
  const minutes = d.getMinutes()
  const ampm = hours >= 12 ? "PM" : "AM"
  const h12 = hours % 12 || 12
  const time = `${h12}:${minutes.toString().padStart(2, "0")} ${ampm}`
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
  const monthStr = months[d.getMonth()]
  const day = d.getDate()

  let interval = seconds / (60 * 60 * 24)
  if (interval < 1) {
    return `Today ${time}`
  } else if (interval >= 1 && interval <= 2) {
    return `Yesterday ${time}`
  }
  return `${monthStr} ${day}, ${d.getFullYear()} ${time}`
}

export const getMembership = (pubnub, uuid) => {
  return pubnub.objects.getMemberships({
    uuid: uuid,
    include: {
      customFields: true
    }
  }).then(res => {
    // eslint-disable-next-line no-console
    console.log("getMemberships raw response:", res)
    return res
  }).catch(err => {
    // eslint-disable-next-line no-console
    console.error("getMemberships error:", err)
    return null
  })
}

export const setMembership = (pubnub, channel) => {
  return pubnub.objects.setMemberships({
    channels: [
      {
        id: channel,
        status: "active" ,
        custom: {
          lastReadTimetoken: Date.now() * 10000
        }
      }
    ]
  })
}

export const getUnreadMessageCounts = (pubnub, channels, timeTokens) => {
  if (channels && channels?.length === timeTokens?.length) {
    return new Promise(resolve => {
      pubnub.messageCounts(
        {
          channels: channels,
          channelTimetokens: timeTokens || []
        },
        (status, results) => {
          resolve(results?.channels || {})
        }
      )
    })
  }
  return {}
}

export const updateChannelLastReadTimetoken = async (pubnub, channelId, uuid) => {
  try {
    const lastReadTimetoken = Math.floor(Date.now() * 10000)
    
    // Update membership's custom field which is used for unread count calculation
    await pubnub.objects.setMemberships({
      uuid: uuid,
      channels: [
        {
          id: channelId,
          custom: {
            lastReadTimetoken: lastReadTimetoken
          }
        }
      ]
    })
    
    // Also update channel metadata to preserve existing custom data
    const channelMetadata = await pubnub.objects.getChannelMetadata({
      channel: channelId,
      include: { customFields: true }
    })
    
    const currentCustom = channelMetadata?.data?.custom || {}
    const userDataStr = currentCustom[uuid]
    const userData = userDataStr ? JSON.parse(userDataStr) : {}
    
    userData.lastReadTimetoken = lastReadTimetoken
    
    return pubnub.objects.setChannelMetadata({
      channel: channelId,
      data: channelMetadata?.data || {},
      custom: {
        ...currentCustom,
        [uuid]: JSON.stringify(userData)
      }
    })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("Failed to update lastReadTimetoken:", err)
  }
}
